import { DeepPartial, DeepReadonly } from 'ts-essentials';
import _has from 'lodash/has';
import { ICTTextBodyProperties, STTextAnchoringType, STTextVerticalType, STTextWrappingType, DeltaOf } from '@tencent/slide-sdk-model';
import { ITextBodyStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { emuToPt, numToPercent, percentToNum, ptToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { VERTICAL_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/vertical-type';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';

/**
 * 文本框属性
 */
export class TextBoxConverter {
    /**
     * 文本框属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        // 上下左右边距
        TextBoxConverter.convertPaddingSdkToUi(sdkData, uiData);
        // 垂直对齐方式
        TextBoxConverter.convertAnchorSdkToUi(sdkData, uiData);
        // 形状中的文字自动换行
        TextBoxConverter.convertWrapSdkToUi(sdkData, uiData);
        // 文字方向
        TextBoxConverter.convertVerticalTypeSdkToUi(sdkData, uiData);
        // 文本自动调整
        TextBoxConverter.convertAutoFitSdkToUi(sdkData, uiData);
        // 文本分栏
        TextBoxConverter.convertColumnsPropsSdkToUi(sdkData, uiData);
        // 3d 文字平面渲染
        TextBoxConverter.convert3dSdkToUi(sdkData, uiData);
        // 文本预设转换
        TextBoxConverter.convertPrstTxWarpSdkToUi(sdkData, uiData);
    }

    /**
     * 文本框属性的ui -> sdk 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        // 上下左右边距
        TextBoxConverter.convertPaddingUiToSdk(uiData, sdkData);
        // 垂直对齐方式
        TextBoxConverter.convertAnchorUiToSdk(uiData, sdkData);
        // 形状中的文字自动换行
        TextBoxConverter.convertWrapUiToSdk(uiData, sdkData);
        // 文字方向
        TextBoxConverter.convertVerticalTypeUiToSdk(uiData, sdkData);
        // 文本自动调整
        TextBoxConverter.convertAutoFitUiToSdk(uiData, sdkData);
        // 文本分栏
        TextBoxConverter.convertColumnsPropsUiToSdk(uiData, sdkData);
        // 3d 文字平面渲染
        TextBoxConverter.convert3dUiToSdk(uiData, sdkData);
        // 文本预设转换
        TextBoxConverter.convertPresetUiToSdk(uiData, sdkData);
    }

    /**
     * 垂直对齐方式
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    public static convertAnchorSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        uiData.bodyStyle = uiData.bodyStyle || {};
        uiData.wrapperStyle = uiData.wrapperStyle || {};
        if (sdkData.anchor) {
            switch (sdkData.anchor) {
                case STTextAnchoringType.t:
                    uiData.wrapperStyle.alignItems = 'flex-start';
                    break;
                case STTextAnchoringType.b:
                    uiData.wrapperStyle.alignItems = 'flex-end';
                    break;
                case STTextAnchoringType.ctr:
                    uiData.wrapperStyle.alignItems = 'center';
                    break;
            }
        }
        if (sdkData.anchorCtr === true || sdkData.anchorCtr === false) {
            const isVert = (sdkData.vert || sdkData.vert === 0) && (sdkData.vert !== STTextVerticalType.horz);
            switch (sdkData.anchorCtr) {
                // 对齐
                default:
                case false:
                    isVert ? uiData.bodyStyle.height = '100%' : uiData.bodyStyle.width = '100%';
                    break;
                // 居中
                case true:
                    if (isVert) {
                        uiData.bodyStyle.height = 'fit-content';
                        uiData.bodyStyle.maxHeight = '100%';
                    } else {
                        uiData.bodyStyle.width = 'fit-content';
                        uiData.bodyStyle.maxWidth = '100%';
                    }
                    uiData.wrapperStyle.justifyContent = 'center';
                    break;
            }
        }
    }

    /**
     * 文字方向
     * @param sdkData
     * @param uiData
     */
    public static convertVerticalTypeSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        uiData.wrapperStyle = uiData.wrapperStyle || {};
        if (sdkData.vert) {
            uiData.wrapperStyle.textDirection = VERTICAL_TYPE_MAP.get(sdkData.vert);
        } else {
            delete uiData.wrapperStyle.textDirection;
        }
    }

    /**
     * 上下左右边距
     * @param sdkData
     * @param uiData
     */
    private static convertPaddingSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        uiData.bodyStyle = uiData.bodyStyle || {};
        if (sdkData.lIns || sdkData.lIns === 0) {
            uiData.bodyStyle.paddingLeft = emuToPt(sdkData.lIns);
        } else {
            delete uiData.bodyStyle.paddingLeft;
        }
        if (sdkData.rIns || sdkData.rIns === 0) {
            uiData.bodyStyle.paddingRight = emuToPt(sdkData.rIns);
        } else {
            delete uiData.bodyStyle.paddingRight;
        }
        if (sdkData.tIns || sdkData.tIns === 0) {
            uiData.bodyStyle.paddingTop = emuToPt(sdkData.tIns);
        } else {
            delete uiData.bodyStyle.paddingTop;
        }
        if (sdkData.bIns || sdkData.bIns === 0) {
            uiData.bodyStyle.paddingBottom = emuToPt(sdkData.bIns);
        } else {
            delete uiData.bodyStyle.paddingBottom;
        }
    }

    /**
     * 形状中的文字自动换行
     * @param sdkData
     * @param uiData
     */
    private static convertWrapSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        uiData.bodyStyle = uiData.bodyStyle || {};
        if (sdkData.wrap || sdkData.wrap === 0) {
            switch (sdkData.wrap) {
                case STTextWrappingType.square:
                    uiData.bodyStyle.whiteSpace = 'pre-wrap';
                    break;
                case STTextWrappingType.none:
                    uiData.bodyStyle.whiteSpace = 'pre';
                    break;
                default:
                    break;
            }
        } else {
            delete uiData.bodyStyle.whiteSpace;
        }
    }

    /**
     * 文本自动调整
     * @param sdkData
     * @param uiData
     */
    private static convertAutoFitSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        uiData.wrapperStyle = uiData.wrapperStyle || {};
        // 类型选择
        if (sdkData.noAutofit) {
            uiData.autoFitType = AutoFitType.NO_AUTO_FIT;
        } else if (sdkData.spAutoFit) {
            uiData.autoFitType = AutoFitType.SHAPE_AUTO_FIT;
        } else if (sdkData.normAutofit) {
            uiData.autoFitType = AutoFitType.FONT_AUTO_FIT;
        } else {
            delete uiData.autoFitType;
        }
        // 缩排文字时的缩放比
        if (sdkData.normAutofit?.fontScale) {
            uiData.wrapperStyle.fontScale = percentToNum(sdkData.normAutofit.fontScale);
        } else {
            delete uiData.wrapperStyle.fontScale;
        }
    }

    private static convert3dSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ) {
        uiData.bodyStyle = uiData.bodyStyle || {};
        if (sdkData.flatTx !== undefined) {
            uiData.bodyStyle.isFlatText = !!sdkData.flatTx;
        } else {
            delete uiData.bodyStyle.isFlatText;
        }
    }


    /**
     * 预设转换属性
     * @param sdkData
     * @param uiData
     */
    private static convertPrstTxWarpSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ) {
        uiData.bodyStyle = uiData.bodyStyle || {};
        if (sdkData.prstTxWarp) {
            uiData.bodyStyle.prstTxWarp = sdkData.prstTxWarp;
        } else {
            delete uiData.bodyStyle.prstTxWarp;
        }
    }

    /**
     * 分栏属性
     * @param sdkData
     * @param uiData
     */
    private static convertColumnsPropsSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBodyProperties>>,
        uiData: ITextBodyStyle,
    ): void  {
        if (sdkData.numCol) {
            uiData.wrapperStyle = uiData.wrapperStyle || {};
            uiData.wrapperStyle.columnNumber = sdkData.numCol;
        } else {
            delete uiData.wrapperStyle?.columnNumber;
        }
        if (sdkData.spcCol) {
            uiData.wrapperStyle = uiData.wrapperStyle || {};
            // 为了避免单位转换过程中带来的精度损失，在 ui 中也使用 emu 单位，只在渲染和展示的时候做转换
            uiData.wrapperStyle.columnSpace = sdkData.spcCol;
        } else {
            delete uiData.wrapperStyle?.columnSpace;
        }
    }

    /**
     * 上下左右边距
     * @param uiData
     * @param sdkData
     */
    private static convertPaddingUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        if (uiData.bodyStyle?.paddingLeft || uiData.bodyStyle?.paddingLeft === 0) {
            sdkData.lIns = ptToEmu(uiData.bodyStyle.paddingLeft);
        }
        if (uiData.bodyStyle?.paddingRight || uiData.bodyStyle?.paddingRight === 0) {
            sdkData.rIns = ptToEmu(uiData.bodyStyle.paddingRight);
        }
        if (uiData.bodyStyle?.paddingTop || uiData.bodyStyle?.paddingTop === 0) {
            sdkData.tIns = ptToEmu(uiData.bodyStyle.paddingTop);
        }
        if (uiData.bodyStyle?.paddingBottom || uiData.bodyStyle?.paddingBottom === 0) {
            sdkData.bIns = ptToEmu(uiData.bodyStyle.paddingBottom);
        }
    }

    /**
     * 垂直对齐方式
     * @param sdkData
     * @param uiData
     */
    private static convertAnchorUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        switch (uiData.wrapperStyle?.alignItems) {
            case 'flex-start':
                sdkData.anchor = STTextAnchoringType.t;
                break;
            case 'flex-end':
                sdkData.anchor = STTextAnchoringType.b;
                break;
            case 'center':
                sdkData.anchor = STTextAnchoringType.ctr;
                break;
        }
        if (uiData.bodyStyle?.height || uiData.bodyStyle?.width) {
            sdkData.anchorCtr = (uiData.bodyStyle?.height || uiData.bodyStyle?.width) === 'fit-content';
        }
    }

    /**
     * 形状中的文字自动换行
     * @param sdkData
     * @param uiData
     */
    private static convertWrapUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        switch (uiData.bodyStyle?.whiteSpace) {
            case 'pre-wrap':
                sdkData.wrap = STTextWrappingType.square;
                break;
            case 'pre':
                sdkData.wrap = STTextWrappingType.none;
                break;
            default:
                break;
        }
    }

    /**
     * 文字方向
     * @param sdkData
     * @param uiData
     */
    private static convertVerticalTypeUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        const direct = uiData.wrapperStyle?.textDirection;
        if (direct) {
            sdkData.vert = invertMap(VERTICAL_TYPE_MAP).get(direct);
        }
    }

    /**
     * 文本自动调整
     * @param sdkData
     * @param uiData
     */
    private static convertAutoFitUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        switch (uiData.autoFitType) {
            case AutoFitType.FONT_AUTO_FIT:
                sdkData.normAutofit = {};
                if (uiData.wrapperStyle?.fontScale) {
                    sdkData.normAutofit.fontScale = numToPercent(uiData.wrapperStyle?.fontScale);
                }
                sdkData.spAutoFit = null;
                sdkData.noAutofit = null;
                break;
            case AutoFitType.SHAPE_AUTO_FIT:
                sdkData.spAutoFit = true;
                sdkData.noAutofit = null;
                break;
            case AutoFitType.NO_AUTO_FIT:
                sdkData.noAutofit = true;
                sdkData.spAutoFit = null;
                break;
            default:
                break;
        }
        if (!uiData.autoFitType && uiData.wrapperStyle?.fontScale) {
            // 非切换文本自动调整  而是在溢出缩排状态下编辑文字，触发了缩放比变化
            sdkData.normAutofit = {
                fontScale: numToPercent(uiData.wrapperStyle?.fontScale),
            };
        }
    }

    /**
     * 文本分栏
     * @param uiData
     * @param sdkData
     */
    private static convertColumnsPropsUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        if (_has(uiData, 'wrapperStyle.columnNumber')) {
            sdkData.numCol = uiData!.wrapperStyle!.columnNumber;
        }
        if (_has(uiData, 'wrapperStyle.columnSpace')) {
            sdkData.spcCol = uiData!.wrapperStyle!.columnSpace;
        }
    }

    private static convert3dUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        if (typeof uiData.bodyStyle?.isFlatText === 'boolean') {
            if (uiData.bodyStyle.isFlatText) {
                sdkData.flatTx = { z: 0 };
            } else {
                sdkData.flatTx = null;
            }
        }
    }

    /**
     * 预设转换属性
     * @param uiData
     * @param sdkData
     */
    private static convertPresetUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextBodyStyle>>,
        sdkData: DeltaOf<ICTTextBodyProperties>,
    ): void {
        if (uiData?.bodyStyle?.prstTxWarp) {
            sdkData.prstTxWarp = uiData.bodyStyle.prstTxWarp;
        }
    }
}
